import { useCallback } from 'react';
import {
  FormControl,
  FormLabel,
  Flex,
  Input,
  Button,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

import SectionCard from '../../components/SectionCard';
import VehicleBasicInfo from './VehicleBasicInfo';
import { useVinLookup, useViccLookup } from '../../hooks';
import type { VehicleCardProps } from './types';

const VehicleCard = ({
  vehicle,
  index,
  quoteId,
  isUpdating,
  onVehicleChange,
  onVinLookupResult,
  onViccLookupResult,
  onModelSelect,
  onManualMakeSelect,
  onManualModelSelect,
  onManualYearSelect,
  onAddRegisteredOwner,
  onRegisteredOwnerChange,
  onRemoveRegisteredOwner,
}: VehicleCardProps) => {
  const {
    isLoading: isVinLoading,
    isPolling: isVinPolling,
    triggerLookup: triggerVinLookup,
  } = useVinLookup({
    quoteId,
    vehicleId: vehicle.id,
    currentVin: vehicle.vin,
    initialResult: vehicle.vin_lookup_result,
    onResult: (result) => {
      onVinLookupResult(index, result, result.vin);
    },
  });

  const {
    isLoading: isViccLoading,
    isPolling: isViccPolling,
    triggerLookup: triggerViccLookup,
  } = useViccLookup({
    quoteId,
    vehicleId: vehicle.id,
    onResult: (result) => {
      if (result.found || result.status === 'found') {
        onViccLookupResult(index, {
          found: true,
          make: result.make,
          model: result.model,
          fuel_type: result.fuel_type,
          available_years: result.available_years,
        });
      }
    },
  });

  const handleVinBlur = useCallback(
    async (vin: string) => {
      if (isUpdating) return;
      await triggerVinLookup(vin);
    },
    [isUpdating, triggerVinLookup],
  );

  const handleViccBlur = useCallback(
    async (viccCode: string) => {
      if (vehicle.vin.length === 17) return;
      await triggerViccLookup(viccCode);
    },
    [vehicle.vin.length, triggerViccLookup],
  );

  const handleModelSelectWrapper = useCallback(
    (viccCode: string) => onModelSelect(index, viccCode),
    [index, onModelSelect],
  );

  const handleManualMakeSelectWrapper = useCallback(
    (manufacturerRef: string, make: string) =>
      onManualMakeSelect(index, manufacturerRef, make),
    [index, onManualMakeSelect],
  );

  const handleManualModelSelectWrapper = useCallback(
    (modelRef: string, model: string) =>
      onManualModelSelect(index, modelRef, model),
    [index, onManualModelSelect],
  );

  const handleManualYearSelectWrapper = useCallback(
    (year: number, vehicleRef: string, fuelType: string) =>
      onManualYearSelect(index, year, vehicleRef, fuelType),
    [index, onManualYearSelect],
  );

  return (
    <SectionCard title="Vehicle" mb={4}>
      <FormControl mb={4}>
        <Flex justify="space-between" mb={2}>
          <FormLabel fontSize="sm" m={0}>
            Registered Owner
          </FormLabel>
          <>
            <IconButton
              aria-label="Add owner"
              icon={<AddIcon />}
              size="sm"
              variant="outline"
              onClick={() => onAddRegisteredOwner(index)}
              display={{ base: 'flex', md: 'none' }}
            />
            <Button
              leftIcon={<AddIcon />}
              size="xs"
              variant="outline"
              onClick={() => onAddRegisteredOwner(index)}
              flexShrink={0}
              display={{ base: 'none', md: 'flex' }}
            >
              Add Registered Owner
            </Button>
          </>
        </Flex>
        {vehicle.registered_owners.map((owner, ownerIdx) => (
          <Flex
            key={ownerIdx}
            gap={2}
            mb={2}
            align="center"
            direction={{ base: 'column', sm: 'row' }}
          >
            {owner.is_business ? (
              <Flex
                gap={2}
                flex="1"
                w={{ base: '100%', sm: 'auto' }}
                align="center"
              >
                {ownerIdx === 0 ? (
                  <Input
                    variant="main"
                    value={owner.business_name || ''}
                    isReadOnly
                    isDisabled
                    size="sm"
                    flex="1"
                    bg="gray.50"
                    _dark={{ bg: 'gray.700' }}
                  />
                ) : (
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" mb={1}>
                      Business Name
                    </FormLabel>
                    <Input
                      variant="main"
                      placeholder="Enter business name"
                      value={owner.business_name || ''}
                      onChange={(e) =>
                        onRegisteredOwnerChange(
                          index,
                          ownerIdx,
                          'business_name',
                          e.target.value,
                        )
                      }
                      size="sm"
                    />
                  </FormControl>
                )}
              </Flex>
            ) : (
              <Flex gap={2} flex="1" w={{ base: '100%', sm: 'auto' }}>
                <Input
                  variant="main"
                  placeholder="First Name"
                  value={owner.first_name}
                  onChange={(e) =>
                    onRegisteredOwnerChange(
                      index,
                      ownerIdx,
                      'first_name',
                      e.target.value,
                    )
                  }
                  size="sm"
                  flex="1"
                />
                <Input
                  variant="main"
                  placeholder="Last Name"
                  value={owner.last_name}
                  onChange={(e) =>
                    onRegisteredOwnerChange(
                      index,
                      ownerIdx,
                      'last_name',
                      e.target.value,
                    )
                  }
                  size="sm"
                  flex="1"
                />
              </Flex>
            )}
            {ownerIdx !== 0 && (
              <IconButton
                aria-label="Remove owner"
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => onRemoveRegisteredOwner(index, ownerIdx)}
                flexShrink={0}
              />
            )}
          </Flex>
        ))}
      </FormControl>

      <VehicleBasicInfo
        vehicle={vehicle}
        index={index}
        onVehicleChange={onVehicleChange}
        isVinLoading={isVinLoading || isVinPolling}
        isViccLoading={isViccLoading || isViccPolling}
        onVinBlur={handleVinBlur}
        onViccBlur={handleViccBlur}
        onModelSelect={handleModelSelectWrapper}
        onManualMakeSelect={handleManualMakeSelectWrapper}
        onManualModelSelect={handleManualModelSelectWrapper}
        onManualYearSelect={handleManualYearSelectWrapper}
      />
    </SectionCard>
  );
};

export default VehicleCard;
